import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import RippleButton from "../../components/common/RippleButton";
import ButtonForAll from "../../components/common/ButtonForAll";
import IconComponent from "../../components/common/IconComponent";
import { FaFilter } from "react-icons/fa";
import { Button } from "@progress/kendo-react-buttons";
import { RxCross1 } from "react-icons/rx";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  clearTourBookingFilterDetails,
  setTourBookingFilterDetails,
} from "./tourBookingSlice";
import { useLocation } from "react-router-dom";
import { ITourBookingFilterDetails } from "./tourBookingModel";

const TourBookingFilter: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const ContactID = location.state?.ContactID;
  const LeadID = location.state?.LeadID;
  const InquiryID = location.state?.InquiryID;
  const QuotationID = location.state?.QuotationID;

  const loading = useAppSelector((state) => state.tourBooking.loading);
  const TourBookingFilterDetails = useAppSelector(
    (state) => state.tourBooking.TourBookingFilterDetails
  );

  const [expand, setExpand] = useState("");
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [TourBookingFilterDetails]);

  useEffect(() => {
    const idMapping: Partial<
      Pick<
        ITourBookingFilterDetails,
        "ContactID" | "LeadID" | "InquiryID" | "QuotationID"
      >
    > = { ContactID, LeadID, InquiryID, QuotationID };

    const updatedFilterDetails = Object.entries(idMapping).reduce(
      (acc, [key, value]) => {
        const typedKey = key as keyof ITourBookingFilterDetails;

        return {
          ...acc,
          [typedKey]: TourBookingFilterDetails?.[typedKey]
            ? `${TourBookingFilterDetails[typedKey]}`
            : value
            ? `${value}`
            : "",
        };
      },
      {} as Partial<ITourBookingFilterDetails>
    );

    if (
      Object.values(updatedFilterDetails).some((value) => value) &&
      JSON.stringify(updatedFilterDetails) !==
        JSON.stringify(TourBookingFilterDetails)
    ) {
      dispatch(
        setTourBookingFilterDetails({
          ...TourBookingFilterDetails,
          ...updatedFilterDetails,
        })
      );
    }
  }, []);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      ContactID: TourBookingFilterDetails?.ContactID
        ? `${TourBookingFilterDetails?.ContactID}`
        : ContactID
        ? `${ContactID}`
        : "",
      LeadID: TourBookingFilterDetails?.LeadID
        ? `${TourBookingFilterDetails?.LeadID}`
        : LeadID
        ? `${LeadID}`
        : "",
      InquiryID: TourBookingFilterDetails?.InquiryID
        ? `${TourBookingFilterDetails?.InquiryID}`
        : InquiryID
        ? `${InquiryID}`
        : "",
      QuotationID: TourBookingFilterDetails?.QuotationID
        ? `${TourBookingFilterDetails?.QuotationID}`
        : QuotationID
        ? `${QuotationID}`
        : "",
      TourBookingNo: values?.TourBookingNo ? values?.TourBookingNo : "",
      FirstName: values?.FirstName ? values?.FirstName : "",
      MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
      Sector: values?.Sector ? values?.Sector : "",
      Tour: values?.Tour ? values?.Tour : "",
      TourDateCode: values?.TourDateCode ? values?.TourDateCode : "",
      GroupBookingNo: "",
      BookBy: values?.BookBy ? values?.BookBy : "",
      Branch: "",
      Limit: 500,
      Skip: 0,
    };
    setExpand("");
    dispatch(setTourBookingFilterDetails(insertPayload));
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExpansionPanel
        className="inquiry-filter-panel m-0"
        title={
          <div>
            <div>
              <IconComponent icon={FaFilter} /> Filter Results
            </div>
          </div>
        }
        subtitle={
          <div>
            <Tooltip anchorElement="target" position="top" parentTitle={true}>
              <Button
                title="Clear"
                themeColor={expand === "filter" ? "light" : "primary"}
                fillMode={"flat"}
                style={{ pointerEvents: "all" }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  dispatch(clearTourBookingFilterDetails());
                }}
              >
                <IconComponent icon={RxCross1} />
              </Button>
            </Tooltip>
          </div>
        }
        expanded={expand === "filter"}
        tabIndex={1}
        onAction={(event: ExpansionPanelActionEvent) => {
          const nativeEvent = event.nativeEvent as KeyboardEvent;
          if (nativeEvent.type === "keydown" && nativeEvent.code === "Space") {
            nativeEvent.preventDefault();
            return;
          }

          if (event?.expanded) {
            setExpand("");
          } else {
            setExpand("filter");
          }
        }}
      >
        {expand === "filter" && (
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={TourBookingFilterDetails}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement>
                <GridLayout
                  style={{ marginRight: 20, padding: 10 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name="TourBookingNo"
                      label="Tour Booking No"
                      placeholder="Tour Booking No"
                      maxLength="100"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="FirstName"
                      label="Name"
                      placeholder="Name"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("FirstName", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="MobileNo"
                      label="Mobile No"
                      type="number"
                      placeholder="i.e. 9999999999"
                      max={10}
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Sector"
                      label="Sector"
                      placeholder="Sector"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("Sector", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Tour"
                      label="Tour"
                      placeholder="Tour"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("Tour", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TourDateCode"
                      label="Tour Date Code"
                      placeholder="Tour Date Code"
                      maxLength="100"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                      marginTop: 20,
                    }}
                  >
                    <ButtonForAll
                      label={"Filter"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                    />

                    <RippleButton
                      type="button"
                      fillMode="outline"
                      themeColor="primary"
                      style={{ marginLeft: 4 }}
                      onClick={() => setExpand("")}
                    >
                      Cancel
                    </RippleButton>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            )}
          />
        )}
      </ExpansionPanel>
    </>
  );
};

export default TourBookingFilter;
